import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Alert, Spinner } from "reactstrap";
import { getCreditNote } from "../service";
import CreditNoteStatusBadge from "../components/CreditNoteStatusBadge";
import CreditNoteBalance from "../components/CreditNoteBalance";
import CreditAdjustmentHistory from "../components/CreditAdjustmentHistory";
import CreditNoteOriginModal from "../components/CreditNoteOriginModal";
import { fmtOrderRupee } from "../../pages/storeSalesDashboardHelpers";

const toErrorText = (err, fallback) => {
  const candidate = err?.response?.data?.error?.message ?? err?.message ?? fallback;
  return typeof candidate === "string" ? candidate : fallback;
};

export default function CreditNoteDetailPage({
  backTo = "/credit-notes",
  backLabel,
}) {
  const { creditNoteId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [originModalOpen, setOriginModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await getCreditNote(creditNoteId);
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) {
          setError(toErrorText(e, "Unable to load credit note."));
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [creditNoteId]);

  const note = data?.credit_note;
  const adjustments = data?.adjustments ?? [];
  const adjustedAmount =
    data?.adjusted_amount ??
    adjustments.reduce((s, a) => s + Number(a.value || 0), 0);
  const remaining = data?.remaining_balance ?? note?.balance ?? 0;
  const detailLinkPrefix = String(backTo || "").includes("dealer")
    ? "/dealer/credit-notes"
    : "/credit-notes";

  return (
    <div className="container py-4">
      <Link
        to={backTo}
        className="small text-decoration-none mb-3 d-inline-block"
      >
        ← {backLabel || "Back to credit notes"}
      </Link>

      {loading ? (
        <div className="py-5 text-center">
          <Spinner />
        </div>
      ) : error ? (
        <Alert color="danger" fade={false}>
          {error}
        </Alert>
      ) : !note ? (
        <Alert color="warning" fade={false}>
          Credit note not found.
        </Alert>
      ) : (
        <>
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
            <div>
              <h2 className="fw-bold mb-2">
                <button
                  type="button"
                  className="btn btn-link p-0 fw-bold text-decoration-none border-0"
                  onClick={() => setOriginModalOpen(true)}
                >
                  {note.identifier}
                </button>
              </h2>
              <CreditNoteStatusBadge status={note.status} />
            </div>
            <div className="text-end">
              <div className="small text-muted mb-1">Balance</div>
              <CreditNoteBalance balance={remaining} className="fs-5" />
            </div>
          </div>

          <div className="border rounded-2 p-3 mb-4 bg-light">
            <div className="fw-semibold mb-3">Financial summary</div>
            <div className="row g-3">
              <div className="col-md-4">
                <div className="small text-muted">Credit value</div>
                <button
                  type="button"
                  className="btn p-0 fw-semibold border-0 text-body text-decoration-none bg-transparent shadow-none"
                  onClick={() => setOriginModalOpen(true)}
                >
                  {fmtOrderRupee(note.value)}
                </button>
              </div>
              <div className="col-md-4">
                <div className="small text-muted">Adjusted amount</div>
                <div className="fw-semibold">{fmtOrderRupee(adjustedAmount)}</div>
              </div>
              <div className="col-md-4">
                <div className="small text-muted">Remaining balance</div>
                <div className="fw-semibold">{fmtOrderRupee(remaining)}</div>
              </div>
            </div>
          </div>

          <div className="fw-semibold mb-2">Adjustment history</div>
          <CreditAdjustmentHistory
            adjustments={adjustments}
            creditNoteValue={note.value}
          />
        </>
      )}

      {note ? (
        <CreditNoteOriginModal
          isOpen={originModalOpen}
          creditNote={note}
          detailLinkPrefix={detailLinkPrefix}
          onClose={() => setOriginModalOpen(false)}
        />
      ) : null}
    </div>
  );
}
